/* eslint-disable  @typescript-eslint/restrict-template-expressions,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-return */
import {
  DocumentResponse,
  MeasurementResponse,
  MeasurementInterface,
  CreateMeasurementRequest,
  SingleUser,
  DossierResponse,
  DossierRelationResponse,
  DossierGroupResponse,
  CreateClientRequest,
  IFindPersonRequest,
  IDossierMetadataResponse,
  IAppointmentResponse,
  IGetCustomAppointmentResponse,
  IUnreadStatus,
  IMultiDossierUnreadStatus,
  UserRelationRoleEnum,
  ISetCustomAppointmentRequest,
  ILibraryItemResponse,
  TFormSubmissionsListResponse,
  ILibraryFormSubmissionResponse,
  ICreateAppointment,
  ICreatePerson,
  UserAccountRoleEnum,
  IUpdatePerson,
} from './types';
import { AuthHelper } from '../helpers/AuthHelper';
import config from '../config.json';
import { DocumentInterface } from '../models/Document';
import { ChatMessageInterface } from '../models/ChatMessage';
import { BehaviorSubject } from 'rxjs';
import { store } from '../app/store';
import { LibraryItem } from '../models/LibraryItem';
import { FormSubmissionListItem, TFormSubmissionsList } from '../models/LibraryFormSubmissionList';
import { LibraryFormSubmission } from '../models/LibraryFormSubmission';
import { IWrittenAppointment, WrittenAppointment } from '../models/WrittenAppointment';
import { DossierMember } from '../models/DossierMember';
import { DossierV2, IDossierV2, IDossierWithOptionalRelation, IDossierWithRelation } from '../models/DossierV2';
import { User } from '../models/User';
import { Account, IAccount } from '../models/Account';
import { UnreadStatus, MultiDossierUnreadStatus } from '../models/UnreadStatus';
import { DossierGroup } from '../models/DossierGroup';
import { ActivityHelper } from '../helpers/ActivityHelper';

const serverUrl = config.apiBasePath;

interface FetchOptions {
	method: string,
	credentials: string,
	cache?: string,
	headers: {
		'Accept'?: string,
		'Content-Type'?: string,
    'Authorization'?: string,
    'x-cognito-token'?: string,
	},
	body?: string
}

const genFetchRequest = (url: string, options: any, query: any = null): Promise<Response> => {
  let address = `${serverUrl}${url}`;
  // console.log('Request start', url); // Enable for debug

  if (query) {
    address += '?';
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    address += Object.keys(query).map((key: string) => `${key}=${query[key]}`).join('&');
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  return fetch(address, options)
    .then(async(response) => {
      if (!response.ok) {
        throw await response.json();
      }
      return response;
    });
};

const genFetch = async<T = any>(url: string, options: FetchOptions, query: any = null): Promise<T> => {
  const request = await genFetchRequest(url, options, query);
  return request.json();
  /* return request.json().then(json => { // Enable for debug
    console.log(url, json);
    return json;
  }); */
};

const genFetchBlob = async(url: string, options: FetchOptions, query: any = null): Promise<Blob> => {
  const request = await genFetchRequest(url, options, query);
  return request.blob();
};

const genOptions = async(method = 'GET', body: any = {}, auth = true, rawBody = false) => {
  const options: FetchOptions = {
    method: method.toUpperCase(),
    credentials: 'omit',
    cache: 'no-cache',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },

  };

  if (auth) {
    const jwtToken = await AuthHelper.getAccessJwtToken();
    options.headers.Authorization = `Bearer ${jwtToken}`;
    options.headers['x-cognito-token'] = jwtToken;
  }

  if (!['GET', 'DELETE'].includes(method)) {
    options.body = rawBody ? body : JSON.stringify(body);
  }

  return options;
};

const genOptionsForm = async(body: FormData, auth = true) => {
  const options = await genOptions('POST', body, auth, true);
  delete options.headers['Content-Type'];
  return options;
};

const genOptionsFileGet = async(auth = true, cache = false) => {
  const options = await genOptions('GET', undefined, auth, true);
  delete options.headers.Accept;
  delete options.headers['Content-Type'];
  if (cache) delete options.cache;
  return options;
};

const encodeFileName = (fileName: string): string => encodeURI(fileName);

let avatarCache: { [userId: number]: Blob|false } = {};
const currentAvatarRequests: { [userId: number]: Promise<Blob> } = {};
const dossierRelationsCache: { [requestHash: string ]: DossierMember[] } = {};

const clearAvatarCache = (): void => {
  avatarCache = {};
  for (const key in currentAvatarRequests) {
    delete currentAvatarRequests[key];
  }
};

const clearDossierRelationsCache = (): void => {
  for (const key in dossierRelationsCache) {
    delete dossierRelationsCache[key];
  }
};

export const clearCaches = (): void => {
  clearAvatarCache();
  clearDossierRelationsCache();
};

export class Api {
  static getAllDossiers = async(): Promise<DossierResponse[]> => genFetch('/administration/dossiers', await genOptions('GET'));

  /**
   * @deprecated use listUserDossiers
   */
  static currentUserFiles = async(): Promise<DossierResponse[]> => genFetch('/dossier', await genOptions('GET', {}));

  /**
   * @deprecated use getDossierV2
   */
  static getDossier = async(dossierId: number): Promise<DossierResponse> => genFetch(`/dossier/${dossierId}`, await genOptions('GET', {}));

  static getDossierGroups = async(dossierId: number): Promise<DossierGroupResponse[]> => genFetch(`/dossier/${dossierId}/groups`, await genOptions('GET', {}));

  /**
   * @deprecated use listDossierRelations
   */
  static getDossierRelations = async(dossierId: number): Promise<DossierRelationResponse[]> => genFetch(`/dossier/${dossierId}/relations`, await genOptions('GET', {}));

  static currentUser = async(): Promise<SingleUser> => genFetch('/user/me', await genOptions('GET', {}));

  static sendMessage = async(groupId: number, message: string): Promise<void> => genFetch(`/chat/${groupId}`, await genOptions('POST', { message }));

  static chatMessages = async(groupId: number): Promise<ChatMessageInterface[]> => genFetch(`/chat/${groupId}`, await genOptions());

  static measurements = async(fileId: number): Promise<MeasurementResponse[]> => genFetch(`/measurement/dossier/${fileId}`, await genOptions());

  static measurementsUnanswered = async(fileId: number): Promise<MeasurementResponse[]> => genFetch(`/measurement/dossier/${fileId}/unanswered`, await genOptions());

  static measurement = async(id: number): Promise<MeasurementResponse> => genFetch(`/measurement/${id}`, await genOptions());

  static deleteMeasurement = async(id: number): Promise<void> => genFetch(`/measurement/${id}`, await genOptions('DELETE'));

  static measurementAnswer = async(id: number, answer: number): Promise<void> => genFetch(`/measurement/${id}/answer`, await genOptions('POST', { answer }));

  static createMeasurement = async(measurement: CreateMeasurementRequest): Promise<MeasurementInterface> => genFetch('/measurement', await genOptions('POST', measurement));

  static listResourceDocuments = async(dossierId: number, memberId: number): Promise<DocumentResponse[]> => genFetch(`/storage/resource/${dossierId}/${memberId}`, await genOptions());

  static deleteResourceDocument = async(dossierId: number, memberId: number, document: DocumentInterface): Promise<void> => genFetch(`/storage/resource/${dossierId}/${memberId}/${encodeFileName(document.name)}`, await genOptions('DELETE'));

  static getResourceDocument = async(dossierId: number, memberId: number, document: DocumentInterface): Promise<Blob> => genFetchBlob(`/storage/resource/${dossierId}/${memberId}/${encodeFileName(document.name)}`, await genOptionsFileGet());

  static uploadResourceDocument = async(dossierId: number, memberId: number, body: FormData): Promise<DocumentResponse> => genFetch(`/storage/resource/${dossierId}/${memberId}`, await genOptionsForm(body));

  static uploadAvatar = async(body: FormData): Promise<void> => {
    avatarCache = {};
    return genFetch('/avatar', await genOptionsForm(body));
  };

  static getAvatar = async(userId: number): Promise<Blob> => {
    const cashedResult = avatarCache[userId];
    if (cashedResult) {
      return cashedResult;
    } else if (cashedResult === false) {
      throw { key: 'AvatarNotFoundError', message: 'Avatar for requested user not found' }; // eslint-disable-line no-throw-literal
    } else if (currentAvatarRequests[userId] !== undefined) {
      return currentAvatarRequests[userId];
    } else {
      try {
        currentAvatarRequests[userId] = genFetchBlob(`/avatar/${userId}`, await genOptionsFileGet(true, false));
        const avatarBlob = await currentAvatarRequests[userId];
        avatarCache[userId] = avatarBlob;
        return avatarBlob;
      } catch (err) {
        const error = err as { key: string };
        if (error.key === 'AvatarNotFoundError') {
          avatarCache[userId] = false;
        }
        throw err;
      } finally {
        delete currentAvatarRequests[userId];
      }
    }
  };

  static listDossierDocuments = async(dossierId: number): Promise<DocumentResponse[]> => genFetch(`/storage/dossier/${dossierId}`, await genOptions());

  static listClientDossierDocuments = async(dossierId: number): Promise<DocumentResponse[]> => genFetch(`/storage/dossier/client/${dossierId}`, await genOptions());

  static listSharedDossierDocuments = async(dossierId: number): Promise<DocumentResponse[]> => genFetch(`/storage/dossier/shared/${dossierId}`, await genOptions());

  static listGlobalDossierDocuments = async(): Promise<DocumentResponse[]> => genFetch('/storage/dossier/global', await genOptions());

  static deleteDossierDocument = async(dossierId: number, document: DocumentInterface): Promise<void> => genFetch(`/storage/dossier/${dossierId}/${encodeFileName(document.name)}`, await genOptions('DELETE'));

  static deleteClientDossierDocument = async(dossierId: number, document: DocumentInterface): Promise<void> => genFetch(`/storage/dossier/client/${dossierId}/${encodeFileName(document.name)}`, await genOptions('DELETE'));

  static deleteSharedDossierDocument = async(dossierId: number, document: DocumentInterface): Promise<void> => genFetch(`/storage/dossier/shared/${dossierId}/${encodeFileName(document.name)}`, await genOptions('DELETE'));

  static getDossierDocument = async(dossierId: number, document: DocumentInterface): Promise<Blob> => genFetchBlob(`/storage/dossier/${dossierId}/${encodeFileName(document.name)}`, await genOptionsFileGet());

  static getClientDossierDocument = async(dossierId: number, document: DocumentInterface): Promise<Blob> => genFetchBlob(`/storage/dossier/client/${dossierId}/${encodeFileName(document.name)}`, await genOptionsFileGet());

  static getSharedDossierDocument = async(dossierId: number, document: DocumentInterface): Promise<Blob> => genFetchBlob(`/storage/dossier/shared/${dossierId}/${encodeFileName(document.name)}`, await genOptionsFileGet());

  static getGlobalDossierDocument = async(document: DocumentInterface): Promise<Blob> => genFetchBlob(`/storage/dossier/global/${encodeFileName(document.name)}`, await genOptionsFileGet());

  static uploadDossierDocument = async(dossierId: number, body: FormData): Promise<DocumentResponse> => genFetch(`/storage/dossier/${dossierId}`, await genOptionsForm(body));

  static uploadClientDossierDocument = async(dossierId: number, body: FormData): Promise<DocumentResponse> => genFetch(`/storage/dossier/client/${dossierId}`, await genOptionsForm(body));

  static uploadSharedDossierDocument = async(dossierId: number, body: FormData): Promise<DocumentResponse> => genFetch(`/storage/dossier/shared/${dossierId}`, await genOptionsForm(body));

  static registerPushEndpoint = async(subscription: PushSubscription): Promise<void> => genFetch('/push/register', await genOptions('POST', subscription));

  static getCaregivers = async(): Promise<SingleUser[]> => genFetch('/administration/caregivers', await genOptions());

  static createClient = async(body: CreateClientRequest): Promise<{ user: SingleUser, dossierId?: number }> => genFetch('/administration/new_client', await genOptions('POST', body));

  static findDossier = async(personInfo: IFindPersonRequest): Promise<IDossierMetadataResponse> => genFetch('/administration/find_dossier', await genOptions('POST', personInfo));

  static getAppointments = async(dossierId: number): Promise<IAppointmentResponse[]> => genFetch(`/appointment/${dossierId}`, await genOptions('GET'));

  static getCustomAppointment = async(relationId: number): Promise<IGetCustomAppointmentResponse> => genFetch(`/appointment/custom/${relationId}`, await genOptions('GET'));

  static setCustomAppointment = async(appointmentInfo: ISetCustomAppointmentRequest): Promise<void> => genFetch('/appointment/custom', await genOptions('POST', appointmentInfo));

  static getUnreadStatus = async(dossierId: number): Promise<UnreadStatus> => genFetch<IUnreadStatus>(`/unread/${dossierId}`, await genOptions('GET'))
    .then(result => new UnreadStatus(result));

  static getUnreadStatusAllDossiers = async(): Promise<MultiDossierUnreadStatus> => genFetch<IMultiDossierUnreadStatus>('/unread/all', await genOptions('GET'))
    .then(result => new MultiDossierUnreadStatus(result));

  static adminGetDossierRelations = async(dossierId: number): Promise<DossierRelationResponse[]> => genFetch(`/administration/dossier/${dossierId}/relations`, await genOptions('GET'));

  static updateDossierRelationRole = async(relationId: number, newRole: UserRelationRoleEnum): Promise<void> => genFetch(`/administration/dossier-relations/${relationId}`, await genOptions('PUT', { role: newRole }))
    .then(() => clearDossierRelationsCache());

  static deleteDossierRelation = async(relationId: number): Promise<void> => genFetch(`/administration/dossier-relations/${relationId}`, await genOptions('DELETE'))
    .then(() => clearDossierRelationsCache());

  static listDossierRelations = async(dossierId: number): Promise<DossierMember[]> => {
    const { user } = store.getState().session;
    const cacheKey = user && `${user.id}-${dossierId}`;

    const cachedRelations = cacheKey && dossierRelationsCache[cacheKey];
    if (cachedRelations) {
      return cachedRelations.map(relation => new DossierMember(relation)); // Return deep copy
    }

    const fetchedRelations = await genFetch<DossierRelationResponse[]>(`/dossier/${dossierId}/relations`, await genOptions('GET', {}))
      .then(relations => DossierMember.fromRequest(relations));
    if (cacheKey) {
      dossierRelationsCache[cacheKey] = fetchedRelations.map(relation => new DossierMember(relation)); // Save deep copy;
    }
    return fetchedRelations;
  };

  static getUserDossierRelation = async(dossierId: number, user?: SingleUser): Promise<DossierMember|undefined> => {
    const relations = await Api.listDossierRelations(dossierId);
    user = user ?? store.getState().session.user;
    if (!user) {
      throw new Error('User not set');
    }
    return relations.find(relation => relation.user.id === user?.id);
  };

  static getLibraryItems = async(relationId: number): Promise<LibraryItem[]> => genFetch<ILibraryItemResponse[]>(`/v2/library/items/${relationId}`, await genOptions('GET'))
    .then(result => result.map(item => new LibraryItem(item)));

  static getAllLibraryItems = async(): Promise<LibraryItem[]> => genFetch<ILibraryItemResponse[]>('/v2/library/items', await genOptions('GET'))
    .then(result => result.map(item => new LibraryItem(item)));

  static getLibraryFormSubmissionsList = async(relationId: number): Promise<TFormSubmissionsList> => genFetch<TFormSubmissionsListResponse>(`/v2/library/submission/list/${relationId}`, await genOptions('GET'))
    .then(result => {
      const keys = Object.keys(result);
      const mapped: TFormSubmissionsList = {};
      keys.forEach(key => {
        const item = result[key];
        mapped[key] = new FormSubmissionListItem(item);
      });
      return mapped;
    });

  static getLibraryFormSubmission = async(submissionId: number): Promise<LibraryFormSubmission> => genFetch<ILibraryFormSubmissionResponse>(`/v2/library/submission/${submissionId}`, await genOptions('GET'))
    .then(result => new LibraryFormSubmission(result));

  static addLibraryItemToDossierRelation = async({ dossierRelationId, libraryItemId }: { dossierRelationId: number, libraryItemId: number }): Promise<void> => (
    genFetch<string>(`/v2/library/items/${dossierRelationId}/${libraryItemId}`, await genOptions('POST')).then(() => undefined)
  );

  static removeLibraryItemFromDossierRelation = async({ dossierRelationId, libraryItemId }: { dossierRelationId: number, libraryItemId: number }): Promise<void> => (
    genFetch<string>(`/v2/library/items/${dossierRelationId}/${libraryItemId}`, await genOptions('DELETE')).then(() => undefined)
  );

  static listAppointments = async(dossierId: number): Promise<WrittenAppointment[]> => genFetch<IWrittenAppointment[]>(`/v2/appointment/${dossierId}`, await genOptions('GET'))
    .then(result => result.map(data => new WrittenAppointment(data)));

  static createAppointment = async(dossierId: number, body: ICreateAppointment): Promise<WrittenAppointment> => genFetch<number>(`/v2/appointment/${dossierId}`, await genOptions('POST', body))
    .then(id => new WrittenAppointment({ id, createdAt: new Date(), ...body }));

  static deleteAppointment = async(appointmentId: number): Promise<void> => genFetch<string>(`/v2/appointment/${appointmentId}`, await genOptions('DELETE'))
    .then(() => undefined); // The endpoint actually returns 'DELETED', so make sure we return nothing

  static createDossier = async(name: string): Promise<DossierV2> => genFetch<number>('/v2/dossier', await genOptions('POST', { name }))
    .then(id => new DossierV2({ id, name, createdAt: new Date(), lastActivityAt: new Date(), archived: false }));

  static updateDossier = async(dossierId: number, name: string): Promise<void> => genFetch<string>(`/v2/dossier/${dossierId}`, await genOptions('PATCH', { name }))
    .then(() => undefined);

  static listDossiers = async(): Promise<DossierV2[]> => genFetch<IDossierV2[]>('/v2/dossier/all', await genOptions('GET'))
    .then(items => items.map(item => new DossierV2(item)));

  static listDossiersWithUserRelation = async(): Promise<IDossierWithOptionalRelation[]> => {
    const dossiers = await Api.listDossiers();
    const requests = dossiers.map(dossier => Api.getDossierWithUserRelation(dossier));
    return Promise.all(requests);
  };

  static listUserDossiers = async(): Promise<DossierV2[]> => genFetch<IDossierV2[]>('/v2/dossier', await genOptions('GET'))
    .then(items => items.map(item => new DossierV2(item)));

  static listUserDossiersWithUserRelation = async(user?: SingleUser): Promise<IDossierWithRelation[]> => {
    const dossiers = await Api.listUserDossiers();
    const requests = dossiers.map(dossier => Api.getDossierWithUserRelation(dossier, user));
    const dossiersWithRelation = await Promise.all(requests);
    for (const item of dossiersWithRelation) {
      if (!item.relation) {
        throw new Error('Expected dossiers to have relation but found dossier without relation');
      }
    }
    return dossiersWithRelation as IDossierWithRelation[];
  };

  static listPersonDossiers = async(personId: number): Promise<DossierV2[]> => genFetch<IDossierV2[]>(`/v2/person/${personId}/dossier`, await genOptions('GET'))
    .then(items => items.map(item => new DossierV2(item)));

  static listPersonDossiersWithUserRelation = async(personId: number): Promise<IDossierWithOptionalRelation[]> => {
    const dossiers = await Api.listPersonDossiers(personId);
    const requests = dossiers.map(dossier => Api.getDossierWithUserRelation(dossier));
    return Promise.all(requests);
  };

  static getDossierV2 = async(dossierId: number): Promise<DossierV2> => genFetch<IDossierV2>(`/v2/dossier/${dossierId}`, await genOptions('GET'))
    .then(item => new DossierV2(item));

  static getDossierWithUserRelation = async(dossier: number|DossierV2, user?: SingleUser): Promise<IDossierWithOptionalRelation> => {
    const dossierObject = dossier instanceof DossierV2 ? dossier : await Api.getDossierV2(dossier);
    const relation = await Api.getUserDossierRelation(dossierObject.id, user);
    const dossierRelations = await Api.listDossierRelations(dossierObject.id);
    return { dossier: dossierObject, relation, dossierRelations };
  };

  static createPerson = async(data: ICreatePerson): Promise<User> => genFetch<number>('/v2/person', await genOptions('POST', data))
    .then(id => new User({ id, name: `${data.firstName} ${data.lastName}`, accountRole: UserAccountRoleEnum.client }));

  static updatePerson = async(personId: number, data: IUpdatePerson): Promise<void> => genFetch<string>(`/v2/person/${personId}`, await genOptions('PATCH', data))
    .then(() => undefined); // Convert the backend response 'UPDATED' into a void response

  /**
   * a.k.a. Create account
   * returns account number
   */
  static invitePerson = async(data: { personId: number, email: string }): Promise<number> => genFetch<number>('/v2/account/invite', await genOptions('POST', data));

  static resetAccountPassword = async(data: { email: string }) => genFetch<{ temporaryPassword: string }>('/v2/account/reset-password', await genOptions('POST', data));

  static listPersons = async(): Promise<User[]> => genFetch<SingleUser[]>('/v2/person', await genOptions('GET')).then(users => users.map(user => new User(user)));

  static getPerson = async(personId: number): Promise<User> => genFetch<SingleUser>(`/v2/person/${personId}`, await genOptions('GET')).then(user => new User(user));

  static getPersonCarewebId = async(personId: number): Promise<number|null> => genFetch<number|null>(`/v2/person/${personId}/careweb-id`, await genOptions('GET'));

  static getPersonAccount = async(personId: number): Promise<Account|null> => genFetch<IAccount|null>(`/v2/person/${personId}/account`, await genOptions('GET'))
    .then(account => (account ? new Account(account) : null));

  static removePersonAccount = async(personId: number): Promise<'deleted'> => genFetch<'deleted'>(`/v2/person/${personId}/account`, await genOptions('DELETE'));

  static addPersonToDossier = async({ dossierId, person, role }: { dossierId: number, person: User, role: UserRelationRoleEnum }): Promise<DossierMember> => (
    genFetch<number>(`/v2/dossier/${dossierId}/person`, await genOptions('POST', { personId: person.id, role }))
      .then(relationId => {
        clearDossierRelationsCache();
        return new DossierMember({ memberId: relationId, role, user: person });
      })
  );

  static notifyNow = async(dossierRelationId: number): Promise<void> => genFetch<string>(`/v2/notification/email/new-activity/${dossierRelationId}`, await genOptions('GET')).then(() => undefined);

  static createChatGroup = async(dossierId: number, name: string, members: DossierMember[]): Promise<DossierGroup> => genFetch<DossierGroupResponse>('/v2/chat/group', await genOptions('POST', {
    dossierId,
    name,
    dossierRelationIds: members.map(member => member.memberId),
  }))
    .then(dossierGroup => DossierGroup.fromRequest([dossierGroup])[0]);

  static addRelationToChatGroup = async(groupId: number, newMember: DossierMember): Promise<void> => genFetch<string>(`/v2/chat/group/${groupId}/member`, await genOptions('POST', { dossierRelationId: newMember.memberId })).then(() => undefined);

  static removeRelationFromChatGroup = async(groupId: number, removeMember: DossierMember): Promise<void> => genFetch<string>(`/v2/chat/group/${groupId}/member/${removeMember.memberId}`, await genOptions('DELETE')).then(() => undefined);

  static archiveGroup = async(groupId: number): Promise<void> => genFetch<string>(`/v2/chat/group/${groupId}`, await genOptions('DELETE')).then(() => undefined);

  static archiveDossier = async(dossierId: number): Promise<void> => genFetch<string>(`/v2/dossier/${dossierId}/archive`, await genOptions('POST')).then(() => undefined);

  static unarchiveDossier = async(dossierId: number): Promise<void> => genFetch<string>(`/v2/dossier/${dossierId}/unarchive`, await genOptions('POST')).then(() => undefined);
}

/*
  This stuff below is because we don't have socket connections yet, so we kinda fake it.
*/
const initialUnreadState = new UnreadStatus({ chat: {} });
export const unreadState$ = new BehaviorSubject<UnreadStatus>(initialUnreadState);

export async function updateUnreadState(): Promise<void> {
  const { session } = store.getState();
  if (session.dossier) {
    const currentState = unreadState$.getValue();
    const result = await Api.getUnreadStatus(session.dossier.id);
    if (JSON.stringify(result) !== JSON.stringify(currentState)) {
      unreadState$.next(result);
    }
  }
}

setInterval(() => {
  if (ActivityHelper.isInactive()) {
    return;
  }

  updateUnreadState().catch(err => console.error('Failed to update unread state', err));
}, 5000);

let previousDossierId: number|undefined;
store.subscribe(() => {
  const { session } = store.getState();
  if (!session.dossier) {
    unreadState$.next(initialUnreadState);
  } else if (session.dossier.id !== previousDossierId) {
    updateUnreadState().catch(err => console.error('Failed to update unread state after file change', err));
  }
  previousDossierId = session?.dossier?.id;
});
